import { action, observable, computed } from "mobx";
import * as constants from "../../constants/constants";
import * as pubSub from "../../common/utils/pub-sub.js";
import { log } from "../../common/utils/console.js";
import {
    getHeaderContainerElement,
    BREAKPOINTS,
    PRODUCT_SELECTOR_OPEN_MODAL,
    PRODUCT_SELECTOR_MODAL_CLOSE,
    PRODUCT_SELECTOR_ADD_PRODUCTS,
    PRODUCT_SELECTOR_PRODUCTS_ADDED,
} from "../../common/constants/index.js";
import { onMediaChange } from "../../common/utils/match-media.js";

import { deepMerge } from "@pes-ui/components/dist/collection/globals/utils";
import { updateProductCtasSelectorSettings } from '@pes-ui/components/dist/collection/components/UI/product-selector/product-selector-utils';
import { productCtas, myProductsResponseStatus } from '@pes-ui/utils/lib/constants';
import { send, productSelectorAddToCartEvent, isAnalyticsEnabled, productSelectorVirtualPageEvent } from '@pes-ui/utils';
import { SC } from '@pes-ui/utils/lib/constants/product-selector';

export class UiStore {
    cardsType = constants.groupType;

    psWrapperInstance = null;

    @observable type = "ALL";

    @observable isMobile = null;

    @observable isMiddleRes = null;

    @observable currentScroll = 0;

    @observable headerHeight = 0;

    rootStore = null;

    @observable extraMenuID = "";

    @observable extraMenuHeight = "";

    @observable isFullWidth = false;

    extraMenuWidth = 300;

    selectorConfiguration = null;

    selectorProductModelLabel = "";

    showNotification = (action) => {
        switch (action) {
            case constants.REMOVE:
                this.notificator.showProductRemoved();
                break;
            case constants.ADD:
                this.notificator.showProductAdded();
                break;
            case constants.ADD_TO_COMPARE:
                this.notificator.showCompareProductAdded();
                break;
            case constants.REMOVE_FROM_COMPARE:
                this.notificator.showCompareProductRemoved();
                break;
            case constants.MAX_PRODUCTS:
                this.notificator.showMaxProductsAdded();
            default:
                log.warn("[UiStore] Unexpected action");
                break;
        }
    };

    constructor(rootStore, selectorConfig) {
        this.rootStore = rootStore;
        this.addDocumentListeners.call(this);
        this.handleResize();
        this.notificator = this.rootStore.helpers.notificator;
        this.onProductSelectorModalClose =
            this.onProductSelectorModalClose.bind(this);

        if (selectorConfig) {
            this.configureSelector(selectorConfig);
        }
    }

    @computed
    get getMobileAppendBlock() {
        if (this.isMobile && this.currentScroll >= this.headerHeight) {
            return $(".my-cart__controls");
        }

        return null;
    }

    @computed
    get getExtraMenuStyles() {
        return {
            maxWidth: this.extraMenuWidth,
            width: this.isFullWidth ? this.extraMenuWidth : "",
            maxHeight: this.extraMenuHeight,
            overflow: "auto",
        };
    }

    addDocumentListeners() {
        window.addEventListener("resize", this.handleResize);
        window.addEventListener("scroll", this.handleScroll);

        onMediaChange(this.recalculateHeader);
    }

    @action.bound
    handleResize() {
        const curWidth = window.innerWidth;

        if (this.notificator) {
            // TODO: this.notificator.hide();
        }

        if (curWidth >= BREAKPOINTS.desktop) {
            this.changeIsMobile(false);
        } else {
            this.changeIsMobile(true);
        }

        this.changeMiddleRes(curWidth);
    }

    @action.bound
    changeMiddleRes(curWidth) {
        if (curWidth < BREAKPOINTS.wideDesktop) {
            this.isMiddleRes = true;
            return;
        }

        this.isMiddleRes = false;
    }

    @action.bound
    handleScroll() {
        if (this.notificator) {
            // this.notificator.hide();
        }

        this.currentScroll = window.scrollY;
        this.handleExtraMenu();
    }

    @action.bound
    changeIsMobile(isMobile) {
        if (this.isMobile !== isMobile) {
            this.recalculateHeader();
            this.isMobile = isMobile;
        }
    }

    @action.bound
    recalculateHeader() {
        const header = getHeaderContainerElement();

        this.headerHeight = (header && header.clientHeight) || 0;
    }

    @action.bound
    changeType(nextType) {
        if (nextType in constants.groupType) {
            this.type = nextType;
        }
    }

    @action.bound
    handleExtraMenu(nextID = "", nextWidth = 300) {
        this.extraMenuID = nextID;
        this.extraMenuWidth = nextWidth;
        this.isFullWidth = false;
    }

    @action.bound
    setMaxHeightExtraMenu(value = "none") {
        this.extraMenuHeight = value;
    }

    onProductSelectorModalClose() {
        this.closeProductSelector();
    }

    openProductSelector(codification, itemTitle = "", selector) {
        const label = this.selectorProductModelLabel + itemTitle;

        if (this.selectorConfiguration.type === SC) {
            this.openSelectorModal(label);
        }

        this.updateSelectorConfiguration(codification, itemTitle, label);
        selector.open(this.selectorConfiguration);
    }

    openSelectorModal(label) {
        const selectorModal = document.createElement('pdp-sc-selector-modal');
        const frame = document.createElement('div');
        frame.id = 'widgetFrame';
        selectorModal.headerLabel = label || 'Select your product';
        selectorModal.appendChild(frame);
        selectorModal.open = true;
        selectorModal.addEventListener('didClose', () => {
            document.body.removeChild(selectorModal);
        });

        document.body.appendChild(selectorModal);
    }

    updateSelectorConfiguration(codification, itemTitle, label) {
        this.selectorConfiguration.scFunctionalIds = JSON.stringify({
            value: codification,
            type: constants.ProductSelectors.CODIFICATION,
        });

        this.selectorConfiguration.scOptions.enableNameConfiguration = itemTitle;
        this.selectorConfiguration.scLabel = label;
    }

    handleAddToCartEvent() {
        document.addEventListener('scSelectorAddToCart', async (res) => {
            const configProductsDetails = res.detail;
            if (configProductsDetails) {
                try {
                    const response = await this.rootStore.services.myProductsAPI.addSCProducts(configProductsDetails);
                    this.onProductsAdded(response);
                } catch (error) {
                    console.error(error);
                }
            }
        });
    }

    updatedSelectorConfig = (config) => {
        return deepMerge(config, { scOptions: this.selectorExtraOptions });
    }

    configureSelector = (response) => {
        const { addToCartProduct, addToProducts } = this.rootStore.labelsStore;
        const configurationObject = {
            "config": response,
            "labels": {
                "addToCartLabel": addToCartProduct,
                "addToMyProductsLabel": addToProducts
            }
        }

        this.selectorExtraOptions = updateProductCtasSelectorSettings(configurationObject, productCtas.buttons);
        this.selectorConfiguration = configurationObject.config;
        this.selectorProductModelLabel = this.selectorConfiguration.scLabel;
        this.handleAddToCartEvent();
    }

    onProductsAdded = (data) => {
        const repositoryId = data.groupId;

        this.rootStore.services.myProductsAPI
            .getProductsFullData()
            .then((responseData) => {
                const groupProduct = responseData.lists.find((p) => p.repositoryId === repositoryId);
                if(groupProduct) {
                    groupProduct?.products.map((product) => {
                        product.price = parseFloatI18n(product.price);
                        return product;
                    });
                }
                this.rootStore.cartStore.addGroup({
                    groupType: constants.groupType.GROUP,
                    item: groupProduct,
                });
                if (isAnalyticsEnabled() && data?.status === myProductsResponseStatus.SUCCESS) {
                    this.sendAnalyticsForAddToMyProducts(data);
                }
            });

    };

    sendAnalyticsForAddToMyProducts = (data) => {
        const { infoForGa } = data;
        const productIds = data.productsUpdated;
        const products = [];
        let currencyCode  =   '';
        productIds.map((id) => {
            const { brand = '', currency = '', description, price = null } = infoForGa[id];
            currencyCode = currency;
            products.push({
                brand,
                price,
                category: '',
                id,
                name: description,
                quantity: 1,
                variant: '',
            });
        });

        send(productSelectorAddToCartEvent, {
            currencyCode,
            distributor: '',
            products,
        });

        send(productSelectorVirtualPageEvent);
    };

    handleSaveConfiguration = (configurationEvent) => {
        pubSub.publish(PRODUCT_SELECTOR_ADD_PRODUCTS, configurationEvent);
        pubSub.subscribe(PRODUCT_SELECTOR_PRODUCTS_ADDED, this.onProductsAdded, {
            unsubscribePrevious: true,
        });
    };

    @action.bound
    closeProductSelector() {
        this.psWrapperInstance.destroy();
    }

    @action.bound
    checkWidth(value = 0) {
        this.isFullWidth = value >= this.extraMenuWidth;
    }
}
